import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import preparePropertyFormSchema from '~/components/Vendors/Properties/helpers/preparePropertyFormSchema';

import { useGetAmenities } from '~/hooks/apis/accommodation/useGetAmenities';
import { useGetPropertiesSchema } from '~/hooks/apis/accommodation/useGetProperty';
import { useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';
import { useSaveOfferBuilderRoom } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import { useGenerateBulkBeddingConfigurations } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';
import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useCreateRoomRate } from '~/hooks/apis/reservation/useCreateRoomRate';
import { useGetRatePlanCreateSchema } from '~/hooks/apis/reservation/usePropertyRatePlans';

import OffersService from '~/services/OffersService';
import PackagesService from '~/services/PackagesService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { mapSFCancellationPolicyToAdmin } from '~/utils/mapSFCancelltionPolicyToAdmin';
import { reportError } from '~/utils/reportError';
import { isSalesforceOpportunityId } from '~/utils/validateSalesforceId';

import ExitConfirmationDialog from './ExitConfirmationDialog';
import PropertyStep from './PropertyStep';
import RatePlanStep from './RatePlanStep';
import {
  FLASH_RATE_PLAN_PRODUCT_TYPE,
  FLASH_RATE_PLAN_RATE_TYPE,
  FLASH_RATE_PLAN_STATUS,
  getDefaultFlashRatePlanName,
  getRatePlanValidationError,
} from './RatePlanStep/utils';
import RoomRateCapacityStep from './RoomRateCapacityStep';
import type { RoomRateCapacityRow } from './RoomRateCapacityStep';
import { getRoomRateCapacityCombinations, getRoomRateOccupancyPayloadByRoomTypeId } from './RoomRateCapacityStep/utils';
import RoomRatesStep from './RoomRatesStep';
import { buildRoomRateRooms, getRoomTypeIdsMatchingSalesforceRoomAssetNames } from './RoomRatesStep/utils';
import RoomsStep from './RoomsStep';
import {
  buildBlankRoomDraft,
  buildRoomCreatePayload,
  getCreatableRoomDrafts,
  getPropertyRoomTypes,
  getSalesforceRoomsMissingExistingRoomTypes,
  mapGeneratedBeddingConfigurationToBedGroups,
  mapSalesforceRoomsToRoomDrafts,
} from './RoomsStep/utils';
import type { FlashOfferRoomDraft } from './RoomsStep/utils';
import TaxesFeesStep from './TaxesFeesStep';
import WizardActions from './WizardActions';
import { CREATE_NEW_PROPERTY, DEFAULT_NEW_PROPERTY_LOGO_ID, STEPS, TAXES_FEES_FORM_ID } from './constants';
import type { AppliedFlashRoomRate, FlashOfferProperty, SetUpFlashOfferSectionProps } from './types';
import {
  buildFlashPackageRoomRateAssignments,
  getFlashRatePlanNameFromTravelStart,
  getMissingRequiredFieldLabels,
  getMissingRequiredPropertyFields,
  getNextButtonLabel,
  mapExtraGuestSurchargeAgeRangesToPropertyFormData,
  mapRedemptionOptionsToPropertyFormData,
  mapVendorBillingToPropertyFormData,
} from './utils';

export default function SetUpFlashOfferSection({
  disabled,
  onPropertiesRefresh,
  properties = [],
  vendor,
  vendorId,
}: SetUpFlashOfferSectionProps) {
  const navigate = useNavigate();
  const defaultPropertyId = properties.find((property) => !property.deleted_at)?.id ?? CREATE_NEW_PROPERTY;
  const [salesforceOfferId, setSalesforceOfferId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const [isCreatingNewProperty, setIsCreatingNewProperty] = useState(false);
  const [isCreatingRatePlan, setIsCreatingRatePlan] = useState(false);
  const [isCreatingRooms, setIsCreatingRooms] = useState(false);
  const [isCreatingRoomRates, setIsCreatingRoomRates] = useState(false);
  const [isApplyingRoomRateCapacity, setIsApplyingRoomRateCapacity] = useState(false);
  const [hasAppliedRoomRates, setHasAppliedRoomRates] = useState(false);
  const [hasAppliedRoomRateCapacity, setHasAppliedRoomRateCapacity] = useState(false);
  const [isValidatingOpportunity, setIsValidatingOpportunity] = useState(false);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPropertyId, setSelectedPropertyId] = useState(defaultPropertyId);
  const [createdProperty, setCreatedProperty] = useState<FlashOfferProperty | null>(null);
  const [createdRatePlan, setCreatedRatePlan] = useState<App.RatePlan | null>(null);
  const [ratePlanName, setRatePlanName] = useState(getDefaultFlashRatePlanName);
  const [ratePlanCancellationPolicy, setRatePlanCancellationPolicy] = useState('');
  const [ratePlanStatus, setRatePlanStatus] = useState(FLASH_RATE_PLAN_STATUS);
  const [ratePlanProductType, setRatePlanProductType] = useState(FLASH_RATE_PLAN_PRODUCT_TYPE);
  const [ratePlanRateType, setRatePlanRateType] = useState(FLASH_RATE_PLAN_RATE_TYPE);
  const [additionalAmenityOptions, setAdditionalAmenityOptions] = useState<Array<string>>([]);
  const [salesforceRooms, setSalesforceRooms] = useState<Array<SalesforceRoom>>([]);
  const [propertyFormData, setPropertyFormData] = useState<Record<string, unknown>>({});
  const [roomDrafts, setRoomDrafts] = useState<Array<FlashOfferRoomDraft>>([]);
  const [appliedRoomRateRoomTypeIds, setAppliedRoomRateRoomTypeIds] = useState<Array<string>>([]);
  const [appliedFlashRoomRates, setAppliedFlashRoomRates] = useState<Array<AppliedFlashRoomRate>>([]);
  const [selectedRoomRateRoomTypeIds, setSelectedRoomRateRoomTypeIds] = useState<Array<string>>([]);
  const [isSavingTaxesFees, setIsSavingTaxesFees] = useState(false);
  const validationRunId = useRef(0);
  const manualRoomDraftCounter = useRef(0);
  const defaultRoomRateSelectionKey = useRef<string | null>(null);
  const trimmedSalesforceOfferId = salesforceOfferId.trim();
  const contentPageUrl = trimmedSalesforceOfferId ? `/offers/${trimmedSalesforceOfferId}/offer-builder/content` : '';
  const isCreatingProperty = selectedPropertyId === CREATE_NEW_PROPERTY;
  const isPropertyStep = activeStep === 1;
  const isRoomStep = activeStep === 2;
  const isRatePlanStep = activeStep === 3;
  const isRoomRatesStep = activeStep === 4;
  const isRoomRateCapacityStep = activeStep === 5;
  const isTaxesAndFeesStep = activeStep === 6;
  const isContentStep = activeStep === STEPS.length - 1;
  const isCreatePropertyStep = isPropertyStep && isCreatingProperty;
  const { refetch: refetchOpportunity } = useGetOpportunity(trimmedSalesforceOfferId);
  const { refetch: refetchOpportunityRelated } = useGetOpportunityRelated(trimmedSalesforceOfferId, { enabled: false });
  const saveRoom = useSaveOfferBuilderRoom();
  const createRoomRate = useCreateRoomRate();
  const generateBulkBeddingConfigurations = useGenerateBulkBeddingConfigurations();
  const {
    data: createPropertySchema,
    isError: hasCreatePropertySchemaError,
    isFetching: isLoadingCreatePropertySchema,
    refetch: refetchCreatePropertySchema,
  } = useGetPropertiesSchema(activeStep === 1 && isCreatingProperty);
  const {
    data: ratePlanCreateSchema,
    isError: hasRatePlanCreateSchemaError,
    isFetching: isLoadingRatePlanCreateSchema,
    refetch: refetchRatePlanCreateSchema,
  } = useGetRatePlanCreateSchema(isRatePlanStep && !createdRatePlan);
  const currentStep = STEPS[activeStep];
  const selectableProperties =
    !createdProperty || properties.some((property) => property.id === createdProperty.id)
      ? properties
      : [...properties, createdProperty];
  const activeProperties = selectableProperties.filter((property) => !property.deleted_at);
  const selectedProperty = selectableProperties.find((property) => property.id === selectedPropertyId) ?? null;
  const selectedPropertyForRooms = createdProperty?.id === selectedPropertyId ? createdProperty : selectedProperty;
  const selectedPropertyRoomTypes = useMemo(
    () => getPropertyRoomTypes(selectedPropertyForRooms),
    [selectedPropertyForRooms],
  );
  const selectedPropertyHasRooms = selectedPropertyRoomTypes.length > 0;
  const roomDraftsToCreate = getCreatableRoomDrafts(roomDrafts);
  const roomRateRooms = useMemo(
    () =>
      buildRoomRateRooms({
        appliedRoomTypeIds: appliedRoomRateRoomTypeIds,
        ratePlanId: createdRatePlan?.id,
        roomDrafts,
        roomTypes: selectedPropertyRoomTypes,
      }),
    [appliedRoomRateRoomTypeIds, createdRatePlan?.id, roomDrafts, selectedPropertyRoomTypes],
  );
  const selectableRoomRateRoomTypeIds = useMemo(
    () => roomRateRooms.filter((room) => !room.hasRatePlan).map((room) => room.id),
    [roomRateRooms],
  );
  const defaultRoomRateRoomTypeIds = useMemo(
    () =>
      getRoomTypeIdsMatchingSalesforceRoomAssetNames({
        roomAssetNames: salesforceRooms.map((room) => room.room_asset?.name),
        rooms: roomRateRooms,
      }),
    [roomRateRooms, salesforceRooms],
  );
  const occupancyPayloadByRoomTypeId = useMemo(
    () =>
      getRoomRateOccupancyPayloadByRoomTypeId({
        roomAssets: salesforceRooms.map((room) => room.room_asset),
        rooms: roomRateRooms,
        selectedRoomTypeIds: appliedFlashRoomRates.map((roomRate) => roomRate.roomTypeId),
      }),
    [appliedFlashRoomRates, roomRateRooms, salesforceRooms],
  );
  const roomRateCapacityRows = useMemo<Array<RoomRateCapacityRow>>(
    () =>
      appliedFlashRoomRates.map((roomRate) => {
        const occupancyPayload = occupancyPayloadByRoomTypeId.get(roomRate.roomTypeId);

        return {
          ...roomRate,
          capacityCombinations: occupancyPayload ? getRoomRateCapacityCombinations(occupancyPayload) : [],
          occupancyPayload,
        };
      }),
    [appliedFlashRoomRates, occupancyPayloadByRoomTypeId],
  );
  const roomRateCapacityRowsToApply = roomRateCapacityRows.filter((roomRate) => roomRate.occupancyPayload);
  const selectedPropertyIdForRooms = selectedPropertyForRooms?.id;
  const {
    data: roomTypesSchema,
    isError: hasRoomTypesSchemaError,
    isFetching: isLoadingRoomTypesSchema,
    refetch: refetchRoomTypesSchema,
  } = useRoomTypesSchema({
    propertyId: selectedPropertyIdForRooms,
    enabled: isRoomStep && !!selectedPropertyIdForRooms,
  });
  const { data: amenityOptionsData = [], isFetching: isLoadingAmenities } = useGetAmenities({ enabled: isRoomStep });
  const roomTypesFormSchema = useMemo<RJSFSchema | undefined>(
    () => (roomTypesSchema?.post?.body?.schema ?? roomTypesSchema?.put?.body?.schema) as RJSFSchema | undefined,
    [roomTypesSchema],
  );
  const amenityOptions = useMemo(() => {
    const fetchedAmenityOptions = (amenityOptionsData as Array<{ name?: string | null }>)
      .map((item) => item.name)
      .filter((name): name is string => typeof name === 'string' && !!name.trim());

    return Array.from(new Set<string>(fetchedAmenityOptions.concat(additionalAmenityOptions))).sort();
  }, [additionalAmenityOptions, amenityOptionsData]);
  const missingRequiredRoomFields = roomDraftsToCreate.flatMap((roomDraft, index) =>
    getMissingRequiredFieldLabels(roomTypesFormSchema, roomDraft).map(
      (fieldLabel) => `Room ${index + 1}: ${fieldLabel}`,
    ),
  );
  const hasInvalidRoomDraft = roomTypesFormSchema
    ? missingRequiredRoomFields.length > 0
    : roomDraftsToCreate.some((roomDraft) => !roomDraft.name.trim());
  const isLoadingRoomDraftForm =
    isRoomStep && roomDrafts.length > 0 && (isLoadingRoomTypesSchema || isLoadingAmenities);
  const hasRoomDraftFormError =
    isRoomStep &&
    roomDrafts.length > 0 &&
    !isLoadingRoomTypesSchema &&
    (hasRoomTypesSchemaError || !roomTypesFormSchema);
  const propertySchema = useMemo(
    () => preparePropertyFormSchema(createPropertySchema, propertyFormData),
    [createPropertySchema, propertyFormData],
  );
  const isLoadingPropertyForm = isCreatingProperty && isLoadingCreatePropertySchema;
  const hasPropertyFormError = isCreatingProperty && hasCreatePropertySchemaError;
  const missingRequiredPropertyFields = getMissingRequiredPropertyFields({
    isCreatePropertyStep,
    propertyFormData,
    propertySchema,
  });
  const hasMissingRequiredPropertyFields = missingRequiredPropertyFields.length > 0;
  const isPropertyStepNextDisabled =
    isCreatePropertyStep &&
    (isLoadingPropertyForm || hasPropertyFormError || !propertySchema || hasMissingRequiredPropertyFields);
  const isRatePlanStepNextDisabled =
    isRatePlanStep &&
    !createdRatePlan &&
    (isCreatingRatePlan ||
      !ratePlanName.trim() ||
      !ratePlanCancellationPolicy.trim() ||
      !ratePlanStatus.trim() ||
      !ratePlanProductType.trim() ||
      !ratePlanRateType.trim() ||
      isLoadingRatePlanCreateSchema ||
      hasRatePlanCreateSchemaError);
  const isRoomStepNextDisabled =
    isRoomStep && (isCreatingRooms || isLoadingRoomDraftForm || hasRoomDraftFormError || hasInvalidRoomDraft);
  const isRoomRatesStepNextDisabled =
    isRoomRatesStep &&
    !hasAppliedRoomRates &&
    (!createdRatePlan?.id || isCreatingRoomRates || selectedRoomRateRoomTypeIds.length === 0);
  const isRoomRateCapacityStepNextDisabled =
    isRoomRateCapacityStep && !hasAppliedRoomRateCapacity && isApplyingRoomRateCapacity;
  const isStepBusy =
    isAdvancingStep ||
    isValidatingOpportunity ||
    isCreatingNewProperty ||
    isCreatingRooms ||
    isCreatingRatePlan ||
    isCreatingRoomRates ||
    isApplyingRoomRateCapacity ||
    isSavingTaxesFees;
  const nextButtonLabel = getNextButtonLabel({
    isAdvancingStep,
    isCreatePropertyStep,
    isCreatingNewProperty,
    isCreatingRooms,
    isRoomStep,
    roomCreateCount: roomDraftsToCreate.length,
    isValidatingOpportunity,
  });
  const primaryButtonLabel =
    isRatePlanStep && !createdRatePlan
      ? isCreatingRatePlan
        ? 'Creating...'
        : 'Create rate plan'
      : isRoomRatesStep
        ? hasAppliedRoomRates
          ? nextButtonLabel
          : isCreatingRoomRates
            ? 'Applying...'
            : 'Apply rate plan'
        : isRoomRateCapacityStep
          ? hasAppliedRoomRateCapacity || roomRateCapacityRowsToApply.length === 0
            ? nextButtonLabel
            : isApplyingRoomRateCapacity
              ? 'Applying...'
              : 'Apply capacity'
          : isTaxesAndFeesStep
            ? isSavingTaxesFees
              ? 'Saving...'
              : 'Save'
            : nextButtonLabel;

  useEffect(() => {
    if (
      isRoomRatesStep &&
      createdRatePlan?.id &&
      roomRateRooms.length > 0 &&
      selectableRoomRateRoomTypeIds.length === 0 &&
      !hasAppliedRoomRates
    ) {
      setHasAppliedRoomRates(true);
    }
  }, [
    createdRatePlan?.id,
    hasAppliedRoomRates,
    isRoomRatesStep,
    roomRateRooms.length,
    selectableRoomRateRoomTypeIds.length,
  ]);

  useEffect(() => {
    if (!isRoomRatesStep || !createdRatePlan?.id || selectableRoomRateRoomTypeIds.length === 0) {
      return;
    }

    const selectionKey = createdRatePlan.id;

    if (defaultRoomRateSelectionKey.current === selectionKey) {
      return;
    }

    defaultRoomRateSelectionKey.current = selectionKey;
    setSelectedRoomRateRoomTypeIds(defaultRoomRateRoomTypeIds);
  }, [createdRatePlan?.id, defaultRoomRateRoomTypeIds, isRoomRatesStep, selectableRoomRateRoomTypeIds.length]);

  useEffect(() => {
    if (isDialogOpen && isContentStep && contentPageUrl) {
      navigate(contentPageUrl);
    }
  }, [contentPageUrl, isContentStep, isDialogOpen, navigate]);

  function goToContentPage() {
    if (contentPageUrl) {
      navigate(contentPageUrl);
    }
  }

  function handleOpen() {
    setIsDialogOpen(true);
    setIsExitConfirmationOpen(false);
    setIsCreatingNewProperty(false);
    setIsCreatingRatePlan(false);
    setIsCreatingRooms(false);
    setIsCreatingRoomRates(false);
    setIsApplyingRoomRateCapacity(false);
    setIsSavingTaxesFees(false);
    setHasAppliedRoomRates(false);
    setHasAppliedRoomRateCapacity(false);
    setIsValidatingOpportunity(false);
    setIsAdvancingStep(false);
    validationRunId.current += 1;
    manualRoomDraftCounter.current = 0;
    defaultRoomRateSelectionKey.current = null;
    setSalesforceOfferId('');
    setErrorMessage(null);
    setActiveStep(0);
    setSelectedPropertyId(defaultPropertyId);
    setCreatedProperty(null);
    setCreatedRatePlan(null);
    setRatePlanName(getDefaultFlashRatePlanName());
    setRatePlanCancellationPolicy('');
    setRatePlanStatus(FLASH_RATE_PLAN_STATUS);
    setRatePlanProductType(FLASH_RATE_PLAN_PRODUCT_TYPE);
    setRatePlanRateType(FLASH_RATE_PLAN_RATE_TYPE);
    setAdditionalAmenityOptions([]);
    setSalesforceRooms([]);
    setPropertyFormData({});
    setRoomDrafts([]);
    setAppliedRoomRateRoomTypeIds([]);
    setAppliedFlashRoomRates([]);
    setSelectedRoomRateRoomTypeIds([]);
  }

  function handleClose() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !trimmedSalesforceOfferId) {
      handleConfirmClose();
      return;
    }

    setIsExitConfirmationOpen(true);
  }

  function handleCancelClose() {
    setIsExitConfirmationOpen(false);
  }

  function handleConfirmClose() {
    validationRunId.current += 1;
    setIsCreatingNewProperty(false);
    setIsCreatingRatePlan(false);
    setIsCreatingRooms(false);
    setIsCreatingRoomRates(false);
    setIsApplyingRoomRateCapacity(false);
    setIsSavingTaxesFees(false);
    setHasAppliedRoomRates(false);
    setHasAppliedRoomRateCapacity(false);
    setIsValidatingOpportunity(false);
    setIsAdvancingStep(false);
    setIsExitConfirmationOpen(false);
    setIsDialogOpen(false);
  }

  function handlePrevious() {
    setErrorMessage(null);
    setActiveStep((prev) => (prev === 3 && selectedPropertyHasRooms ? 1 : Math.max(prev - 1, 0)));
  }

  function handlePropertyCreateStart() {
    setErrorMessage(null);
    setIsCreatingNewProperty(true);
  }

  function handlePropertyCreateError() {
    setIsCreatingNewProperty(false);
    setErrorMessage('Unable to create property.');
  }

  async function getRoomsWithBeddingConfigurations(rooms: Array<SalesforceRoom>) {
    const bedGroupsByDealId = new Map<string, SalesforceRoom['bed_groups']>();
    const dealIdByRoomAssetId = new Map<string, string>();
    let hasBeddingConfigurationError = false;

    rooms.forEach((room) => {
      const roomAssetId = room.room_asset?.sfid || room.room_asset?.id;
      const roomAssetKey = roomAssetId ? String(roomAssetId) : '';
      const dealId = room.id ? String(room.id) : '';

      if (roomAssetKey && dealId && !dealIdByRoomAssetId.has(roomAssetKey)) {
        dealIdByRoomAssetId.set(roomAssetKey, dealId);
      }
    });

    const dealIds = Array.from(dealIdByRoomAssetId.values());

    for (let index = 0; index < dealIds.length; index += 10) {
      let response;

      try {
        response = await generateBulkBeddingConfigurations.mutateAsync(dealIds.slice(index, index + 10));
      } catch (error) {
        reportError(error);
        hasBeddingConfigurationError = true;
        continue;
      }

      response.result?.forEach((result) => {
        if (result.status !== 200) {
          hasBeddingConfigurationError = true;
          return;
        }

        const bedGroups = mapGeneratedBeddingConfigurationToBedGroups({ result: result.result });

        if (bedGroups.length > 0) {
          bedGroupsByDealId.set(result.deal_offer_id, bedGroups);
        }
      });
    }

    if (hasBeddingConfigurationError) {
      setErrorMessage('Some bedding configurations could not be prefilled. Review room bedding before continuing.');
    }

    return rooms.map((room) => {
      const bedGroups = bedGroupsByDealId.get(String(room.id));
      return bedGroups ? { ...room, bed_groups: bedGroups } : room;
    });
  }

  async function prepareRoomDrafts(property: FlashOfferProperty | null, existingRoomTypes: Array<App.RoomType> = []) {
    if (!property?.id) {
      setRoomDrafts([]);
      return [];
    }

    const missingSalesforceRooms = getSalesforceRoomsMissingExistingRoomTypes(salesforceRooms, existingRoomTypes);

    if (missingSalesforceRooms.length === 0) {
      setRoomDrafts([]);
      return [];
    }

    const rooms = await getRoomsWithBeddingConfigurations(missingSalesforceRooms);
    const roomDrafts = mapSalesforceRoomsToRoomDrafts({
      propertyCategory: property.category,
      propertyId: property.id,
      rooms,
    });

    setRoomDrafts(roomDrafts);
    return roomDrafts;
  }

  async function handlePropertyCreateSuccess(property: FlashOfferProperty) {
    if (!property?.id) {
      handlePropertyCreateError();
      return;
    }

    setCreatedProperty(property);
    setSelectedPropertyId(property.id);
    setErrorMessage(null);
    setIsAdvancingStep(true);

    try {
      await prepareRoomDrafts(property);
      await refreshProperties(property);
      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    } finally {
      setIsAdvancingStep(false);
      setIsCreatingNewProperty(false);
    }
  }

  function updateRoomDraft(draftId: string, updates: Partial<FlashOfferRoomDraft>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => (roomDraft.draftId === draftId ? { ...roomDraft, ...updates } : roomDraft)),
    );
  }

  function handleAddAmenityOption(amenityName: string) {
    const trimmedAmenityName = amenityName.trim();

    if (!trimmedAmenityName) {
      return;
    }

    setAdditionalAmenityOptions((prev) => (prev.includes(trimmedAmenityName) ? prev : prev.concat(trimmedAmenityName)));
  }

  function handleAddRoomDraft() {
    const propertyId = selectedPropertyForRooms?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before adding rooms.');
      return;
    }

    manualRoomDraftCounter.current += 1;
    setRoomDrafts((prev) => [
      ...prev,
      buildBlankRoomDraft({
        draftId: `manual-room-${manualRoomDraftCounter.current}`,
        propertyCategory: selectedPropertyForRooms.category,
        propertyId,
      }),
    ]);
  }

  function handleRemoveRoomDraft(draftId: string) {
    setRoomDrafts((prev) => prev.filter((roomDraft) => roomDraft.draftId !== draftId));
  }

  function markRoomDraftsCreated(savedRoomsByDraftId: Map<string, App.RoomType>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => {
        const savedRoom = savedRoomsByDraftId.get(roomDraft.draftId);

        if (!savedRoom) {
          return roomDraft;
        }

        return {
          ...roomDraft,
          id: savedRoom.id,
          existingRoomName: savedRoom.name || roomDraft.name,
          shouldCreate: false,
        };
      }),
    );
  }

  async function createSelectedRooms() {
    const propertyId = selectedPropertyForRooms?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before creating rooms.');
      return false;
    }

    if (hasRoomDraftFormError) {
      setErrorMessage('Unable to load room fields.');
      return false;
    }

    if (hasInvalidRoomDraft) {
      setErrorMessage(`Complete mandatory room fields: ${missingRequiredRoomFields.join(', ') || 'Name'}.`);
      return false;
    }

    if (roomDraftsToCreate.length === 0) {
      return true;
    }

    const savedRoomsByDraftId = new Map<string, App.RoomType>();

    setIsCreatingRooms(true);
    setErrorMessage(null);

    try {
      for (const roomDraft of roomDraftsToCreate) {
        const savedRoom = await saveRoom.mutateAsync({
          offerId: trimmedSalesforceOfferId,
          propertyId,
          room: buildRoomCreatePayload(roomDraft, propertyId),
          suppressNotifications: true,
        });
        savedRoomsByDraftId.set(roomDraft.draftId, savedRoom);
      }

      markRoomDraftsCreated(savedRoomsByDraftId);
      await refreshProperties(selectedPropertyForRooms ?? undefined, Array.from(savedRoomsByDraftId.values()));
      return true;
    } catch (error) {
      markRoomDraftsCreated(savedRoomsByDraftId);
      setErrorMessage(`Unable to create rooms. ${getErrorMessage(error)}`);
      return false;
    } finally {
      setIsCreatingRooms(false);
    }
  }

  async function refreshProperties(property?: FlashOfferProperty, roomTypes?: Array<App.RoomType>) {
    try {
      await onPropertiesRefresh?.(property, roomTypes);
    } catch (error) {
      reportError(error);
    }
  }

  function handleRatePlanCreateSuccess(ratePlan: App.RatePlan) {
    setCreatedRatePlan(ratePlan);
    setIsCreatingRatePlan(false);
    setErrorMessage(null);
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  function handleToggleRoomRateRoom(roomTypeId: string) {
    setErrorMessage(null);
    setSelectedRoomRateRoomTypeIds((prev) =>
      prev.includes(roomTypeId) ? prev.filter((id) => id !== roomTypeId) : prev.concat(roomTypeId),
    );
  }

  async function applyRoomRates() {
    if (!createdRatePlan?.id) {
      setErrorMessage('Create a Flash rate plan before applying room rates.');
      return false;
    }

    if (selectedRoomRateRoomTypeIds.length === 0) {
      setErrorMessage('Select at least one room.');
      return false;
    }

    const propertyId = selectedPropertyForRooms?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before applying room rates.');
      return false;
    }

    setIsCreatingRoomRates(true);
    setErrorMessage(null);

    const createdRoomRateRoomTypeIds: Array<string> = [];
    const createdRoomRates: Array<AppliedFlashRoomRate> = [];

    try {
      for (const roomTypeId of selectedRoomRateRoomTypeIds) {
        const response = await createRoomRate.mutateAsync({
          propertyId,
          ratePlanId: createdRatePlan.id,
          roomTypeId,
        });

        const roomRateId = response?.result?.id;

        if (!roomRateId) {
          throw new Error('Unexpected response from createRoomRate');
        }

        createdRoomRateRoomTypeIds.push(roomTypeId);
        createdRoomRates.push({
          ratePlanName: createdRatePlan.name,
          roomName: roomRateRooms.find((room) => room.id === roomTypeId)?.name || roomTypeId,
          roomRateId,
          roomTypeId,
        });
      }

      setAppliedRoomRateRoomTypeIds((prev) => Array.from(new Set(prev.concat(selectedRoomRateRoomTypeIds))));
      setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates));
      setHasAppliedRoomRates(true);
      return true;
    } catch (error) {
      setAppliedRoomRateRoomTypeIds((prev) => Array.from(new Set(prev.concat(createdRoomRateRoomTypeIds))));
      setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates));
      setSelectedRoomRateRoomTypeIds((prev) => prev.filter((id) => !createdRoomRateRoomTypeIds.includes(id)));
      reportError(error);
      setErrorMessage(`Unable to apply rate plan. ${getErrorMessage(error)}`);
      return false;
    } finally {
      setIsCreatingRoomRates(false);
    }
  }

  async function applyRoomRateCapacity() {
    const propertyId = selectedPropertyForRooms?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before applying capacity.');
      return false;
    }

    if (roomRateCapacityRowsToApply.length === 0) {
      setHasAppliedRoomRateCapacity(true);
      return true;
    }

    setIsApplyingRoomRateCapacity(true);
    setErrorMessage(null);

    try {
      for (const roomRate of roomRateCapacityRowsToApply) {
        if (!roomRate.occupancyPayload) {
          continue;
        }

        await ReservationService.updateRoomTypeOccupancy(
          roomRate.occupancyPayload,
          propertyId,
          roomRate.roomTypeId,
          roomRate.roomRateId,
        );
      }

      setHasAppliedRoomRateCapacity(true);
      return true;
    } catch (error) {
      reportError(error);
      setErrorMessage(`Unable to apply capacity. ${getErrorMessage(error)}`);
      return false;
    } finally {
      setIsApplyingRoomRateCapacity(false);
    }
  }

  async function handleRatePlanSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedRatePlanName = ratePlanName.trim();
    const trimmedCancellationPolicy = ratePlanCancellationPolicy.trim();
    const trimmedStatus = ratePlanStatus.trim();
    const trimmedProductType = ratePlanProductType.trim();
    const trimmedRateType = ratePlanRateType.trim();

    const validationError = getRatePlanValidationError({
      ratePlanCancellationPolicy,
      ratePlanName,
      ratePlanProductType,
      ratePlanRateType,
      ratePlanStatus,
    });
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsCreatingRatePlan(true);
    setErrorMessage(null);

    try {
      const response = await ReservationService.createRatePlan({
        id_salesforce_external: vendorId,
        name: trimmedRatePlanName,
        cancellation_policy: trimmedCancellationPolicy,
        status: trimmedStatus,
        product_type: trimmedProductType,
        rate_type: trimmedRateType,
      });

      if (!response?.result?.id) {
        throw new Error('Unexpected response from createRatePlan');
      }

      handleRatePlanCreateSuccess(response.result);
    } catch (error) {
      reportError(error);
      setIsCreatingRatePlan(false);
      setErrorMessage('Unable to create rate plan.');
    }
  }

  async function assignRoomRatesToPackages() {
    if (!selectedPropertyForRooms?.id || appliedFlashRoomRates.length === 0) {
      return;
    }

    setIsAdvancingStep(true);
    setErrorMessage(null);

    try {
      const response = await OffersService.getOffer(trimmedSalesforceOfferId);
      const assignments = buildFlashPackageRoomRateAssignments({
        offerPackages: response?.result?.packages,
        relatedPackageRooms: salesforceRooms,
        roomRates: appliedFlashRoomRates,
        roomTypes: selectedPropertyRoomTypes,
      });

      const results = await Promise.allSettled(
        assignments.map((assignment) =>
          PackagesService.updatePackage(assignment.packageData, trimmedSalesforceOfferId, assignment.packageId),
        ),
      );

      results.forEach((result) => {
        if (result.status === 'rejected') {
          reportError(result.reason);
        }
      });
    } catch (error) {
      reportError(error);
    } finally {
      setIsAdvancingStep(false);
    }
  }

  async function handleNext() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !isSalesforceOpportunityId(trimmedSalesforceOfferId)) {
      setErrorMessage('Enter a valid Salesforce offer ID.');
      return;
    }

    if (activeStep === 0) {
      const runId = validationRunId.current + 1;
      validationRunId.current = runId;
      setIsValidatingOpportunity(true);
      const { data: opportunity, error } = await refetchOpportunity();

      if (runId !== validationRunId.current) {
        return;
      }

      if (error || !opportunity?.account_18_digit_id__c) {
        setIsValidatingOpportunity(false);
        setErrorMessage(`Unable to validate the Salesforce offer ID. ${getErrorMessage(error)}`);
        return;
      }

      const opportunityVendorId = opportunity.account_18_digit_id__c;

      if (opportunityVendorId !== vendorId) {
        setIsValidatingOpportunity(false);
        setErrorMessage('This Salesforce offer does not belong to this vendor.');
        return;
      }

      setRatePlanCancellationPolicy(
        mapSFCancellationPolicyToAdmin(opportunity.functional_details?.cancellation_policy__c ?? undefined) ?? '',
      );
      const { data: opportunityRelated } = await refetchOpportunityRelated().catch((error) => {
        reportError(error);
        return { data: undefined };
      });

      if (runId !== validationRunId.current) {
        return;
      }

      const travelStartRatePlanName = getFlashRatePlanNameFromTravelStart(opportunityRelated?.deals);
      setRatePlanName(travelStartRatePlanName || getDefaultFlashRatePlanName());

      setPropertyFormData({
        id_salesforce_external: vendorId,
        ...mapVendorBillingToPropertyFormData(vendor),
        logo_id_cloudinary_external: DEFAULT_NEW_PROPERTY_LOGO_ID,
        ...mapRedemptionOptionsToPropertyFormData(opportunity.key_commercial?.redemption_options__c),
        ...mapExtraGuestSurchargeAgeRangesToPropertyFormData(opportunityRelated?.extra_guest_surcharges),
      });
      setSalesforceRooms(opportunityRelated?.deals ?? opportunityRelated?.rooms ?? []);
      setRoomDrafts([]);
      setIsValidatingOpportunity(false);
    }

    if (activeStep === 1 && isCreatingNewProperty) {
      return;
    }

    if (activeStep === 1 && isLoadingPropertyForm) {
      setErrorMessage('Wait for property details to finish loading.');
      return;
    }

    if (activeStep === 1 && missingRequiredPropertyFields.length > 0) {
      setErrorMessage(`Complete mandatory property fields: ${missingRequiredPropertyFields.join(', ')}.`);
      return;
    }

    if (activeStep === 1 && isCreatingProperty && !propertySchema) {
      setErrorMessage('Unable to validate property details.');
      return;
    }

    if (activeStep === 1 && !isCreatingProperty) {
      setIsAdvancingStep(true);

      try {
        const preparedRoomDrafts = await prepareRoomDrafts(selectedPropertyForRooms, selectedPropertyRoomTypes);

        if (selectedPropertyHasRooms && preparedRoomDrafts.length === 0) {
          setErrorMessage(null);
          setActiveStep(3);
          return;
        }
      } finally {
        setIsAdvancingStep(false);
      }
    }

    if (activeStep === 2) {
      const roomsCreated = await createSelectedRooms();

      if (!roomsCreated) {
        return;
      }
    }

    if (activeStep === 4 && !hasAppliedRoomRates) {
      const roomRatesCreated = await applyRoomRates();

      if (!roomRatesCreated) {
        return;
      }
    }

    if (activeStep === 5 && !hasAppliedRoomRateCapacity && roomRateCapacityRowsToApply.length > 0) {
      const capacityApplied = await applyRoomRateCapacity();

      if (!capacityApplied) {
        return;
      }
    }

    if (activeStep === 6) {
      await assignRoomRatesToPackages();
    }

    const shouldPreserveRoomDraftWarning = activeStep === 1 && !isCreatingProperty && !selectedPropertyHasRooms;
    if (!shouldPreserveRoomDraftWarning) {
      setErrorMessage(null);
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={isDialogOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton
            aria-label="Close"
            disabled={isStepBusy}
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack spacing={2}>
            <Typography variant="h6">{currentStep.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentStep.helperText}
            </Typography>

            {activeStep === 0 && (
              <TextField
                label="Salesforce offer ID"
                value={salesforceOfferId}
                onChange={(event) => {
                  setSalesforceOfferId(event.target.value);
                  setCreatedRatePlan(null);
                  setRatePlanName(getDefaultFlashRatePlanName());
                  setRatePlanCancellationPolicy('');
                  setErrorMessage(null);
                }}
                required
                fullWidth
                autoFocus
                disabled={isStepBusy}
              />
            )}

            {activeStep === 1 && (
              <PropertyStep
                activeProperties={activeProperties}
                hasPropertyFormError={hasPropertyFormError}
                isBusy={isStepBusy}
                isCreatingProperty={isCreatingProperty}
                isLoadingPropertyForm={isLoadingPropertyForm}
                onDraftChange={setPropertyFormData}
                onPropertyCreateError={handlePropertyCreateError}
                onPropertyCreateStart={handlePropertyCreateStart}
                onPropertyCreateSuccess={handlePropertyCreateSuccess}
                onPropertySelect={(propertyId) => {
                  setErrorMessage(null);
                  setSelectedPropertyId(propertyId);
                  setRoomDrafts([]);
                }}
                onRetryPropertySchema={() => {
                  setErrorMessage(null);
                  refetchCreatePropertySchema();
                }}
                propertyFormData={propertyFormData}
                propertySchema={propertySchema}
                selectedPropertyId={selectedPropertyId}
                vendorId={vendorId}
              />
            )}

            {isRatePlanStep && (
              <RatePlanStep
                createdRatePlan={createdRatePlan}
                hasSchemaError={hasRatePlanCreateSchemaError}
                isCreatingRatePlan={isCreatingRatePlan}
                isLoadingSchema={isLoadingRatePlanCreateSchema}
                onCancellationPolicyChange={setRatePlanCancellationPolicy}
                onClearError={() => setErrorMessage(null)}
                onProductTypeChange={setRatePlanProductType}
                onRatePlanNameChange={setRatePlanName}
                onRateTypeChange={setRatePlanRateType}
                onRetrySchema={refetchRatePlanCreateSchema}
                onStatusChange={setRatePlanStatus}
                onSubmit={handleRatePlanSubmit}
                ratePlanCancellationPolicy={ratePlanCancellationPolicy}
                ratePlanCreateSchema={ratePlanCreateSchema}
                ratePlanName={ratePlanName}
                ratePlanProductType={ratePlanProductType}
                ratePlanRateType={ratePlanRateType}
                ratePlanStatus={ratePlanStatus}
              />
            )}

            {activeStep === 2 && (
              <RoomsStep
                amenityOptions={amenityOptions}
                hasRoomDraftFormError={hasRoomDraftFormError}
                isBusy={isStepBusy}
                isLoadingRoomDraftForm={isLoadingRoomDraftForm}
                isLoadingRoomTypesSchema={isLoadingRoomTypesSchema}
                onAddAmenityOption={handleAddAmenityOption}
                onAddRoomDraft={handleAddRoomDraft}
                onRemoveRoomDraft={handleRemoveRoomDraft}
                onRetryLoadRoomFields={() => {
                  setErrorMessage(null);
                  refetchRoomTypesSchema();
                }}
                onUpdateRoomDraft={updateRoomDraft}
                property={selectedPropertyForRooms}
                roomDrafts={roomDrafts}
                roomTypesFormSchema={roomTypesFormSchema}
              />
            )}

            {isRoomRatesStep && (
              <RoomRatesStep
                createdRatePlan={createdRatePlan}
                disabled={isStepBusy}
                onClearRooms={() => {
                  setErrorMessage(null);
                  setSelectedRoomRateRoomTypeIds([]);
                }}
                onSelectAllRooms={() => {
                  setErrorMessage(null);
                  setSelectedRoomRateRoomTypeIds(selectableRoomRateRoomTypeIds);
                }}
                onToggleRoom={handleToggleRoomRateRoom}
                readOnly={hasAppliedRoomRates}
                rooms={roomRateRooms}
                selectedRoomTypeIds={selectedRoomRateRoomTypeIds}
              />
            )}

            {isRoomRateCapacityStep && (
              <RoomRateCapacityStep capacityRows={roomRateCapacityRows} readOnly={hasAppliedRoomRateCapacity} />
            )}

            {isTaxesAndFeesStep && (
              <TaxesFeesStep
                disabled={isStepBusy}
                formId={TAXES_FEES_FORM_ID}
                onSaved={() => {
                  setErrorMessage(null);
                  setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
                }}
                onSavingChange={setIsSavingTaxesFees}
                propertyId={selectedPropertyForRooms?.id}
              />
            )}

            {isContentStep && contentPageUrl && (
              <Alert severity="info">
                Redirecting to the{' '}
                <Link component="button" onClick={goToContentPage} underline="hover">
                  Offer Builder content page
                </Link>
                . If it does not open automatically, use the button below.
              </Alert>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <WizardActions
          activeStep={activeStep}
          contentPageUrl={contentPageUrl}
          isContentStep={isContentStep}
          isCreatePropertyStep={isCreatePropertyStep}
          isPropertyStepNextDisabled={isPropertyStepNextDisabled}
          isRatePlanStep={isRatePlanStep}
          isRatePlanStepNextDisabled={isRatePlanStepNextDisabled}
          isRoomRateCapacityStepNextDisabled={isRoomRateCapacityStepNextDisabled}
          isRoomRatesStepNextDisabled={isRoomRatesStepNextDisabled}
          isRoomStepNextDisabled={isRoomStepNextDisabled}
          isStepBusy={isStepBusy}
          isTaxesAndFeesStep={isTaxesAndFeesStep}
          onGoToContentPage={goToContentPage}
          onNext={handleNext}
          onPrevious={handlePrevious}
          primaryButtonLabel={primaryButtonLabel}
          ratePlanCreated={!!createdRatePlan}
          trimmedSalesforceOfferId={trimmedSalesforceOfferId}
        />
      </Dialog>

      <ExitConfirmationDialog
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        open={isExitConfirmationOpen}
      />
    </>
  );
}
